import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Edit, Printer, MessageCircle, History } from "lucide-react";
import { api, loadAuthToken } from "../lib/api";
import { isAdmin, photoUrl, waLink, fmtDate } from "../lib/customers";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Avatar } from "../components/CustomerForm";
import { CustomerEditor } from "../components/customer/CustomerEditor";
import { PersonalTab } from "../components/customer/PersonalTab";
import { DocumentsTab } from "../components/customer/DocumentsTab";
import { WorksTab } from "../components/customer/WorksTab";
import { NotesTab } from "../components/customer/NotesTab";
import { CustomerPrint } from "../components/customer/PrintViews";

const AuditDialog = ({ cid, onClose }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/customers/${cid}/audit`).then((r) => setItems(r.data || [])).catch(() => {}); }, [cid]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-dialog">
        <DialogHeader><DialogTitle>Activity Log</DialogTitle></DialogHeader>
        <div className="divide-y divide-slate-100 text-sm">
          {items.map((a) => (
            <div key={a.id} className="py-2 flex justify-between gap-3">
              <div><span className="font-medium">{a.action}</span> <span className="text-slate-600">{a.detail}</span></div>
              <div className="text-xs text-slate-500 text-right whitespace-nowrap">{a.by} ({a.role})<br />{fmtDate(a.at, true)}</div>
            </div>
          ))}
          {!items.length && <div className="py-6 text-center text-slate-500">Koi activity nahi</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function CustomerProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [c, setC] = useState(null);
  const [works, setWorks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [editing, setEditing] = useState(false);
  const [audit, setAudit] = useState(false);
  const printing = sp.get("print") === "1";

  const load = async () => {
    try { const r = await api.get(`/customers/${id}`); setC(r.data); }
    catch (e) { if (e?.response?.status === 401) navigate("/admin/login"); else setC(false); }
  };
  const loadWorks = () => api.get(`/customers/${id}/works`).then((r) => setWorks(r.data || [])).catch(() => {});
  const loadNotes = () => api.get(`/customers/${id}/notes`).then((r) => setNotes(r.data || [])).catch(() => {});
  useEffect(() => {
    if (!loadAuthToken()) { navigate("/admin/login"); return; }
    load(); loadWorks(); loadNotes();
    // eslint-disable-next-line
  }, [id]);

  if (c === false) return <div className="max-w-xl mx-auto py-16 text-center text-slate-500" data-testid="customer-not-found">Customer nahi mila ya aapko access nahi hai.</div>;
  if (!c) return <div className="py-16 text-center text-slate-400">Loading…</div>;

  return (
    <div data-testid="customer-profile-page" className="max-w-6xl mx-auto px-4 lg:px-8 py-8">
      <Button variant="ghost" size="sm" onClick={() => navigate("/admin/customers")} className="mb-4" data-testid="profile-back"><ArrowLeft className="w-4 h-4 mr-1" /> Customer Details</Button>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-wrap items-center gap-5">
        <Avatar src={photoUrl(c)} name={c.name} size="w-24 h-24" testid="profile-avatar" />
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs font-semibold tracking-widest text-teal-700" data-testid="profile-code">{c.customer_code}</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900" data-testid="profile-name">{c.name}</h1>
          <div className="text-sm text-slate-600 mt-1">{c.mobile} · {c.branch_name || "Main (Head Office)"}{c.father_name ? ` · S/o ${c.father_name}` : ""}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {waLink(c) && <a href={waLink(c)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 h-9 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm" data-testid="profile-wa-btn"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
          <Button variant="outline" onClick={() => setEditing(true)} data-testid="profile-edit-btn"><Edit className="w-4 h-4 mr-1" /> Edit</Button>
          <Button className="bg-teal-700 hover:bg-teal-800 text-white" onClick={() => setSp({ print: "1" })} data-testid="profile-print-btn"><Printer className="w-4 h-4 mr-1" /> Print Customer Details</Button>
          {isAdmin() && <Button variant="outline" onClick={() => setAudit(true)} data-testid="profile-audit-btn"><History className="w-4 h-4 mr-1" /> Activity</Button>}
        </div>
      </div>

      <Tabs defaultValue="personal" className="mt-6">
        <TabsList className="flex w-full overflow-x-auto justify-start sm:grid sm:grid-cols-4">
          <TabsTrigger value="personal" data-testid="tab-personal">Personal Details</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="works" data-testid="tab-works">Service Details ({works.length})</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes ({notes.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="personal"><PersonalTab c={c} /></TabsContent>
        <TabsContent value="documents"><DocumentsTab c={c} /></TabsContent>
        <TabsContent value="works"><WorksTab c={c} works={works} reload={loadWorks} /></TabsContent>
        <TabsContent value="notes"><NotesTab c={c} notes={notes} works={works} reload={loadNotes} /></TabsContent>
      </Tabs>

      {editing && <CustomerEditor customer={c} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} onOpenExisting={(x) => navigate(`/admin/customers/${x}`)} />}
      {audit && <AuditDialog cid={c.id} onClose={() => setAudit(false)} />}
      {printing && <CustomerPrint c={c} notes={notes} works={works} onClose={() => setSp({})} />}
    </div>
  );
}
